/**
 * <local-team>
 *
 * Reveals one team — the visitor's nearest — on a card that ships hidden.
 *
 * How the visitor's location is obtained is a data and privacy decision, not a
 * coding one, so this element deliberately ships with no resolver enabled:
 * `data-source="off"` is the default and the card never appears. The one
 * resolver implemented here needs no vendor, sends nothing off the page and
 * only ever runs after the visitor asks for it by name.
 *
 * It also needs coordinates on each team. A team without them is skipped, so
 * where no team has coordinates none is ever nearest and the card stays hidden
 * rather than guessing — a licensed brand mark shown against the wrong team is
 * the failure worth avoiding here.
 *
 * The resolved team is deliberately not persisted. Every page load re-asks for
 * a position and re-runs the search, so the card always reflects where the
 * visitor is now and a team can never outlive the data it was picked from. The
 * costs are that the reveal is always asynchronous — there is no remembered
 * team to paint immediately — and that the browser is queried once per page
 * view rather than once per visitor.
 */
const EARTH_RADIUS_KM = 6371;

/**
 * The card is for United States and Canada visitors only, so the position the
 * browser reports is bounds-checked before a team is ever chosen. Boxes are
 * [southLat, westLng, northLat, eastLng]; a position inside any one of them
 * passes.
 *
 * Rectangles, not real borders, because resolving a coordinate to a country
 * properly means sending it to a geocoding service — and not sending the
 * visitor's position anywhere is the whole point of this resolver. The
 * approximation is deliberately generous at the edges: it cleanly excludes
 * Europe, Asia, Africa, Oceania and South America, which is the job, while
 * admitting northern Mexico, the Bahamas and western Greenland. The cost of
 * those is a border visitor seeing a nearby US team, which is a far smaller
 * failure than wrongly hiding the card from someone in Texas.
 *
 * Not covered, and one line each to add if they ever matter: the Aleutians
 * west of the antimeridian, and US territories such as Puerto Rico and Guam.
 */
const SUPPORTED_REGIONS = [
  [24.4, -125.0, 49.4, -66.9], // contiguous United States
  [51.2, -168.2, 71.5, -129.9], // Alaska
  [18.9, -160.3, 22.3, -154.8], // Hawaii
  [41.7, -141.1, 83.2, -52.6], // Canada
];

/** ISO country codes the card is offered in, matched against Shopify's IP-detected country. */
const SUPPORTED_COUNTRIES = ['US', 'CA'];

const COUNTRY_STORAGE_KEY = 'local-team-country';

if (!customElements.get('local-team')) {
  customElements.define(
    'local-team',
    class LocalTeam extends HTMLElement {
      constructor() {
        super();
        this.onRetry = this.onRetry.bind(this);
        this.onPermissionChange = this.onPermissionChange.bind(this);
      }

      connectedCallback() {
        this.teams = this.readIndex();
        this.crest = this.querySelector('[data-local-team-crest]');
        this.nameLead = this.querySelector('[data-local-team-name-lead]');
        this.nameRest = this.querySelector('[data-local-team-name-rest]');
        this.link = this.querySelector('.local-team__button');
        this.retry = this.querySelector('[data-local-team-retry]');

        if (this.retry) this.retry.addEventListener('click', this.onRetry);

        if (this.dataset.source !== 'browser') return;

        this.start();
      }

      disconnectedCallback() {
        this.abortController?.abort();
        if (this.retry) this.retry.removeEventListener('click', this.onRetry);
        if (this.permission) this.permission.removeEventListener('change', this.onPermissionChange);
      }

      async start() {
        const country = await this.detectCountry();

        if (country && !SUPPORTED_COUNTRIES.includes(country)) {
          console.info(`local-team: visitor detected in ${country}, outside the United States and Canada, so the card stays hidden.`);
          this.dismiss();
          return;
        }

        // Hidden by a saved country that is no longer true: bring the skeleton
        // back. Without located teams it was hidden by Liquid, and stays so.
        if (this.hidden && this.teams.some((team) => LocalTeam.hasCoordinates(team))) {
          this.dataset.state = 'loading';
          this.setAttribute('aria-busy', 'true');
          this.hidden = false;
        }

        this.watchPermission();
        this.locate();
      }

      /** Returns the IP-detected country code and saves it, or null if the lookup fails. */
      async detectCountry() {
        this.abortController?.abort();
        this.abortController = new AbortController();

        try {
          const url = new URL(`${window.Shopify?.routes?.root || '/'}browsing_context_suggestions.json`, window.location.origin);
          const response = await fetch(url, { signal: this.abortController.signal });
          if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

          const data = await response.json();
          const country = data?.detected_values?.country?.handle || null;
          if (country) LocalTeam.saveCountry(country);

          return country;
        } catch (error) {
          if (error.name !== 'AbortError') console.info('local-team: could not detect the visitor country', error);
          return null;
        }
      }

      static saveCountry(country) {
        try {
          localStorage.setItem(COUNTRY_STORAGE_KEY, country);
        } catch (error) {
          console.info('local-team: could not save the visitor country', error);
        }
      }

      readIndex() {
        const script = this.querySelector('[data-local-team-index]');
        if (!script) return [];

        try {
          const parsed = JSON.parse(script.textContent);
          return Array.isArray(parsed) ? parsed.filter((team) => team && team.name) : [];
        } catch (error) {
          console.error('local-team: could not read the team index', error);
          return [];
        }
      }

      /**
       * The card ships in its loading state and settles into exactly one of two
       * outcomes: a team, or gone. `dismiss` is the second — every dead end
       * routes through it so the skeleton can never be left shimmering at
       * something that is not coming.
       */
      dismiss() {
        this.hidden = true;
        this.removeAttribute('data-state');
        this.removeAttribute('aria-busy');
      }

      /**
       * `fresh` refuses the browser's cached position fix. The automatic call
       * on load accepts one up to ten minutes old, which is ample for choosing
       * between teams hundreds of kilometres apart and avoids waking the
       * device's location hardware on every page view. "Try again" has to ask
       * again for real: with no remembered team left to clear, it would
       * otherwise repeat the load-time lookup against the same cached fix and
       * return the same team, making the link look broken.
       *
       * Every outcome other than success leaves the card hidden, which is the
       * right thing for a visitor and indistinguishable from every other
       * outcome for whoever is debugging it. So each dead end says which one it
       * was: one console line, at most once per page view, only when a resolver
       * is switched on. Four different causes used to look like nothing
       * happening.
       */
      locate({ fresh = false } = {}) {
        if (!navigator.geolocation) {
          console.info('local-team: this browser has no geolocation, so the card stays hidden.');
          this.dismiss();
          return;
        }

        if (!this.teams.some((team) => LocalTeam.hasCoordinates(team))) {
          console.info('local-team: no team has latitude and longitude, so none can be nearest.');
          this.dismiss();
          return;
        }

        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;

            // Outside the US and Canada the card is not offered at all, so the
            // search is skipped rather than run and discarded.
            if (!LocalTeam.inSupportedRegion(latitude, longitude)) {
              console.info(
                `local-team: ${latitude}, ${longitude} is outside the United States and Canada, so no team is offered. A VPN does not change this — the browser reports the device's own position, not the exit node's.`,
              );
              this.dismiss();
              return;
            }

            const team = this.nearest(latitude, longitude);
            if (!team) {
              console.info(`local-team: no team matched ${latitude}, ${longitude}.`);
              this.dismiss();
              return;
            }

            this.show(team);
          },
          (error) => {
            /* Declined, unavailable or timed out: the card stays hidden, which
               is the same state a visitor who has never been located sees. */
            console.info(`local-team: no position available (${error.code}: ${error.message}), so the card stays hidden.`);
            this.dismiss();
          },
          // No `timeout` on the automatic call. Safari counts the time the
          // permission prompt sits on screen against it, so the 8s limit
          // expired while the visitor was still reading the dialog: the error
          // callback ran, and granting permission a moment later had nothing
          // left listening — which is why the card only appeared on reload.
          // The retry keeps a limit, because permission is already granted by
          // then and nothing is waiting on a human.
          fresh ? { maximumAge: 0, timeout: 8000 } : { maximumAge: 600000 },
        );
      }

      /**
       * Grants that arrive after the request has already settled — the prompt
       * answered late, or permission given from the address bar without a
       * reload — leave the card hidden with nothing watching. This re-runs the
       * search the moment permission turns into a yes.
       *
       * Progressive enhancement: browsers without the Permissions API, or
       * without 'geolocation' as a queryable name, simply keep the behaviour
       * they had. Dropping the timeout above is what fixes the common case.
       */
      async watchPermission() {
        if (!navigator.permissions?.query) return;

        try {
          this.permission = await navigator.permissions.query({ name: 'geolocation' });
        } catch (error) {
          /* Older Safari rejects the name outright rather than reporting it as
             unsupported. Nothing to do — the timeout change covers this. */
          return;
        }

        this.permission.addEventListener('change', this.onPermissionChange);
      }

      onPermissionChange() {
        if (this.permission?.state !== 'granted') return;

        // Already showing a team: re-running would only re-prompt the position
        // provider for an answer that is on screen.
        if (!this.hidden) return;

        this.locate();
      }

      /**
       * A team with no coordinates serialises as `"lat": null`, and both
       * `Number(null)` and `Number('')` are 0 — a finite number — so testing
       * with `Number.isFinite` alone put every unset team on the equator at
       * (0, 0) rather than skipping it. With no coordinates anywhere, all 120
       * then tied at the same distance and the first one in the index was
       * returned as the visitor's nearest team, which is exactly the wrong
       * crest this section exists to avoid showing. Every empty shape has to
       * be rejected before the value is trusted as a number.
       */
      static coordinate(value) {
        if (value === null || value === undefined || value === '') return null;

        const number = Number(value);
        return Number.isFinite(number) ? number : null;
      }

      static hasCoordinates(team) {
        return LocalTeam.coordinate(team.lat) !== null && LocalTeam.coordinate(team.lng) !== null;
      }

      /** True when the position falls inside any SUPPORTED_REGIONS box. */
      static inSupportedRegion(latitude, longitude) {
        return SUPPORTED_REGIONS.some(
          ([south, west, north, east]) => latitude >= south && latitude <= north && longitude >= west && longitude <= east,
        );
      }

      nearest(latitude, longitude) {
        let closest = null;
        let shortest = Infinity;

        for (const team of this.teams) {
          const teamLat = LocalTeam.coordinate(team.lat);
          const teamLng = LocalTeam.coordinate(team.lng);
          if (teamLat === null || teamLng === null) continue;

          const distance = LocalTeam.distance(latitude, longitude, teamLat, teamLng);
          if (distance < shortest) {
            shortest = distance;
            closest = team;
          }
        }

        return closest;
      }

      /** Great-circle distance in km. Only the ordering matters, but haversine is cheap. */
      static distance(fromLat, fromLng, toLat, toLng) {
        const toRadians = (degrees) => (degrees * Math.PI) / 180;
        const deltaLat = toRadians(toLat - fromLat);
        const deltaLng = toRadians(toLng - fromLng);
        const a = Math.sin(deltaLat / 2) ** 2 + Math.cos(toRadians(fromLat)) * Math.cos(toRadians(toLat)) * Math.sin(deltaLng / 2) ** 2;

        return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(a));
      }

      /**
       * The frame sets the first word of the name in bold and the rest in a
       * lighter weight — "BUFFALO" then "BISONS". A single-word name keeps the
       * bold half and leaves the light half empty, which CSS then collapses.
       */
      show(team) {
        const [lead, ...rest] = team.name.trim().split(/\s+/);

        if (this.nameLead) this.nameLead.textContent = lead || '';
        if (this.nameRest) this.nameRest.textContent = rest.join(' ');

        if (this.crest) {
          if (team.crest) {
            this.crest.src = team.crest;
            this.crest.alt = team.name;
            this.crest.hidden = false;
          } else {
            this.crest.removeAttribute('src');
            this.crest.hidden = true;
          }
        }

        // Same tab. Team URLs are this store's own collections written as full
        // myshopify.com addresses, so an origin test called them external on any
        // other host (the custom domain, the theme preview) and opened a tab.
        if (this.link && team.url) this.link.href = team.url;

        // Settled: the skeleton gives way to the team, the team half becomes
        // focusable again, and assistive tech is told the wait is over.
        this.dataset.state = 'ready';
        this.removeAttribute('aria-busy');
        this.querySelector('.local-team__team')?.removeAttribute('inert');
        this.hidden = false;
      }

      onRetry() {
        // Back to the skeleton rather than to nothing: the card stays where it
        // is while the position is re-read, so the band does not collapse and
        // reflow the page under the visitor who just clicked.
        this.dataset.state = 'loading';
        this.setAttribute('aria-busy', 'true');
        this.querySelector('.local-team__team')?.setAttribute('inert', '');
        this.locate({ fresh: true });
      }
    },
  );
}
